import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { FiEdit2, FiCheck, FiUser, FiCalendar, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import './HR.css';

const MOOD_CFG = {
  great:   { emoji:'😄', label:'Great',    color:'#10b981' },
  good:    { emoji:'😊', label:'Good',     color:'#3b82f6' },
  okay:    { emoji:'😐', label:'Okay',     color:'#f59e0b' },
  stressed:{ emoji:'😰', label:'Stressed', color:'#f97316' },
  blocked: { emoji:'😤', label:'Blocked',  color:'#ef4444' },
};

const TODAY = new Date().toISOString().slice(0, 10);

export default function DailyReports() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'manager';
  const [tab,       setTab]     = useState(isAdmin ? 'team' : 'my');
  const [myReport,  setMyReport]= useState(null);
  const [myTasks,   setMyTasks] = useState([]);
  const [form,      setForm]    = useState({ summary:'', blockers:'', tomorrow:'', mood:'good', hoursWorked:'', tasksDone:[] });
  const [saving,    setSaving]  = useState(false);
  const [reports,   setReports] = useState([]);
  const [members,   setMembers] = useState([]);
  const [filterDate, setFilterDate] = useState(TODAY);
  const [filterUser, setFilterUser] = useState('');
  const [expanded,  setExpanded] = useState({});
  const [autoFill,  setAutoFill] = useState(false);

  useEffect(() => {
    loadMyReport();
    loadMyTasks();
    if (isAdmin) {
      api.get('/users').then(r => setMembers((r.data.users||[]).filter(u=>u.role!=='client')));
    }
  }, []);

  useEffect(() => {
    if (tab === 'team') loadTeamReports();
  }, [tab, filterDate, filterUser]);

  const loadMyReport = async () => {
    try {
      const r = await api.get('/daily-reports/today');
      if (r.data.report) {
        setMyReport(r.data.report);
        setForm({
          summary: r.data.report.summary || '',
          blockers: r.data.report.blockers || '',
          tomorrow: r.data.report.tomorrow || '',
          mood: r.data.report.mood || 'good',
          hoursWorked: r.data.report.hoursWorked || '',
          tasksDone: r.data.report.tasksDone || [],
        });
      }
    } catch {}
  };

  const loadMyTasks = async () => {
    try {
      const r = await api.get('/tasks?limit=100');
      const tasks = (r.data.tasks || []).filter(t => t.status !== 'completed');
      setMyTasks(tasks);
    } catch {}
  };

  const loadTeamReports = async () => {
    try {
      const params = { date: filterDate };
      if (filterUser) params.userId = filterUser;
      const r = await api.get('/daily-reports', { params });
      setReports(r.data.reports || []);
    } catch { toast.error('Failed to load reports'); }
  };

  const autoFillFromTimeLogs = async () => {
    try {
      const r = await api.get(`/timelogs?userId=${user.id}&date=${TODAY}`);
      const logs = r.data.timelogs || r.data.data || [];
      if (!logs.length) return toast.error('No time logs found for today');
      const auto = logs.map(l => `• ${l.task?.title || l.description || 'Work'}: ${((l.duration||0)/3600).toFixed(1)}h`).join('\n');
      setForm(f => ({ ...f, summary: auto }));
      toast.success('Auto-filled from time logs!');
    } catch { toast.error('Could not auto-fill'); }
  };

  const submit = async () => {
    if (!form.summary.trim()) return toast.error('Summary is required');
    setSaving(true);
    try {
      const r = await api.post('/daily-reports', { ...form, date: TODAY });
      setMyReport(r.data.report);
      toast.success(r.data.created ? 'Report submitted!' : 'Report updated!');
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to submit'); }
    setSaving(false);
  };

  const TA = (extra={}) => ({ width:'100%', padding:'10px 13px', border:'1.5px solid #e2e8f0', borderRadius:10, fontSize:13, outline:'none', boxSizing:'border-box', fontFamily:'inherit', resize:'vertical', ...extra });

  return (
    <div className="hr-page">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
        <div>
          <h1 style={{ margin:0 }}>Daily Work Reports</h1>
          <p style={{ margin:'4px 0 0', color:'#94a3b8', fontSize:13 }}>Submit your end-of-day work summary</p>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          {['my', ...(isAdmin ? ['team'] : [])].map(t => (
            <button key={t} onClick={() => setTab(t)}
              style={{ padding:'9px 20px', borderRadius:10, border:'none', fontWeight:700, fontSize:13, cursor:'pointer',
                background: tab===t ? 'var(--pm-gradient)' : '#f1f5f9', color: tab===t ? '#fff' : '#64748b' }}>
              {t === 'my' ? '📝 My Report' : '👥 Team Reports'}
            </button>
          ))}
        </div>
      </div>

      {/* My Report */}
      {tab === 'my' && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 340px', gap:20 }}>
          <div className="card" style={{ padding:28 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <h3 style={{ margin:0, fontWeight:700 }}>
                {myReport ? '✏️ Update Today\'s Report' : '📝 Submit Daily Report'}
                <span style={{ fontSize:13, fontWeight:400, color:'#94a3b8', marginLeft:8 }}>{new Date().toLocaleDateString('en-IN',{weekday:'long',day:'numeric',month:'long'})}</span>
              </h3>
              <button onClick={autoFillFromTimeLogs} style={{ padding:'7px 14px', borderRadius:9, border:'1.5px solid #e2e8f0', background:'#fff', fontSize:12, fontWeight:600, cursor:'pointer', color:'#667eea' }}>
                ⚡ Auto-fill from time logs
              </button>
            </div>

            <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#64748b', marginBottom:5 }}>What did you work on today? *</label>
            <textarea style={{ ...TA(), minHeight:120 }} value={form.summary} onChange={e=>setForm(f=>({...f,summary:e.target.value}))} placeholder="• Completed the login page redesign&#10;• Fixed bug in invoice PDF export&#10;• Reviewed 3 PRs"/>

            <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#64748b', marginBottom:5, marginTop:16 }}>Blockers / Issues (optional)</label>
            <textarea style={{ ...TA(), minHeight:70 }} value={form.blockers} onChange={e=>setForm(f=>({...f,blockers:e.target.value}))} placeholder="Any blockers, delays, or dependencies holding you back?"/>

            <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#64748b', marginBottom:5, marginTop:16 }}>Plan for tomorrow (optional)</label>
            <textarea style={{ ...TA(), minHeight:70 }} value={form.tomorrow} onChange={e=>setForm(f=>({...f,tomorrow:e.target.value}))} placeholder="• Start working on dashboard charts&#10;• Meeting with client at 3PM"/>

            {/* Quick task checkboxes */}
            {myTasks.length > 0 && (
              <div style={{ marginTop:16 }}>
                <label style={{ fontSize:11, fontWeight:700, color:'#64748b', display:'block', marginBottom:8 }}>Mark tasks completed today</label>
                <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                  {myTasks.slice(0,10).map(t => {
                    const sel = form.tasksDone.includes(t._id||t.id);
                    return (
                      <span key={t._id||t.id} onClick={() => setForm(f => ({ ...f, tasksDone: sel ? f.tasksDone.filter(x=>x!==(t._id||t.id)) : [...f.tasksDone, t._id||t.id] }))}
                        style={{ padding:'5px 12px', borderRadius:20, fontSize:12, fontWeight:600, cursor:'pointer', border:'1.5px solid', borderColor: sel?'#10b981':'#e2e8f0', background: sel?'#d1fae5':'#f8fafc', color: sel?'#065f46':'#475569' }}>
                        {sel && '✓ '}{t.title.slice(0,30)}{t.title.length>30?'…':''}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            <button onClick={submit} disabled={saving}
              style={{ marginTop:24, padding:'11px 28px', background:'var(--pm-gradient)', color:'#fff', border:'none', borderRadius:12, fontWeight:700, fontSize:14, cursor:'pointer', display:'flex', alignItems:'center', gap:8 }}>
              <FiCheck/> {saving ? 'Saving…' : myReport ? 'Update Report' : 'Submit Report'}
            </button>
          </div>

          {/* Mood + Hours sidebar */}
          <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
            <div className="card" style={{ padding:20 }}>
              <h4 style={{ margin:'0 0 14px', fontWeight:700 }}>How was your day?</h4>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                {Object.entries(MOOD_CFG).map(([v,c]) => (
                  <button key={v} onClick={() => setForm(f=>({...f,mood:v}))}
                    style={{ padding:'10px 8px', borderRadius:10, border:`2px solid ${form.mood===v?c.color:'#e2e8f0'}`, background: form.mood===v?`${c.color}15`:'#fff', cursor:'pointer', fontWeight:600, fontSize:13, textAlign:'center' }}>
                    <div style={{ fontSize:22 }}>{c.emoji}</div>
                    <div style={{ fontSize:11, color:c.color, marginTop:2 }}>{c.label}</div>
                  </button>
                ))}
              </div>
            </div>
            <div className="card" style={{ padding:20 }}>
              <h4 style={{ margin:'0 0 12px', fontWeight:700 }}>Hours Worked</h4>
              <input type="number" step="0.5" min="0" max="24"
                style={{ width:'100%', padding:'10px 13px', border:'1.5px solid #e2e8f0', borderRadius:10, fontSize:16, fontWeight:700, outline:'none', boxSizing:'border-box', textAlign:'center' }}
                value={form.hoursWorked} onChange={e=>setForm(f=>({...f,hoursWorked:e.target.value}))} placeholder="8"/>
              <p style={{ margin:'8px 0 0', fontSize:11, color:'#94a3b8', textAlign:'center' }}>hours today</p>
            </div>
            {myReport && (
              <div style={{ padding:14, background:'#f0fdf4', borderRadius:12, border:'1px solid #bbf7d0' }}>
                <div style={{ fontWeight:700, color:'#065f46', fontSize:13 }}>✅ Report submitted</div>
                <div style={{ fontSize:11, color:'#6b7280', marginTop:2 }}>Last updated {new Date(myReport.submittedAt||myReport.updatedAt).toLocaleTimeString()}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Team Reports (admin) */}
      {tab === 'team' && isAdmin && (
        <div>
          <div style={{ display:'flex', gap:10, marginBottom:20, flexWrap:'wrap' }}>
            <input type="date" style={{ padding:'8px 14px', border:'1.5px solid #e2e8f0', borderRadius:10, fontSize:13, outline:'none' }}
              value={filterDate} onChange={e=>setFilterDate(e.target.value)}/>
            <select style={{ padding:'8px 14px', border:'1.5px solid #e2e8f0', borderRadius:10, fontSize:13, outline:'none', background:'#fff' }}
              value={filterUser} onChange={e=>setFilterUser(e.target.value)}>
              <option value="">All Employees</option>
              {members.map(m=><option key={m._id||m.id} value={m._id||m.id}>{m.name}</option>)}
            </select>
            <span style={{ alignSelf:'center', fontSize:13, color:'#94a3b8' }}>{reports.length} report(s)</span>
          </div>

          {reports.length === 0 ? (
            <div className="card" style={{ padding:60, textAlign:'center', color:'#94a3b8' }}>
              <FiCalendar size={40} style={{ display:'block', margin:'0 auto 12px' }}/>
              No reports submitted for this date
            </div>
          ) : reports.map(r => {
            const mc = MOOD_CFG[r.mood] || MOOD_CFG.good;
            const exp = expanded[r.id];
            return (
              <div key={r.id} className="card" style={{ marginBottom:12, padding:0, overflow:'hidden' }}>
                <div onClick={() => setExpanded(e=>({...e,[r.id]:!e[r.id]}))}
                  style={{ display:'flex', alignItems:'center', gap:14, padding:'16px 20px', cursor:'pointer', userSelect:'none' }}>
                  <div style={{ width:36, height:36, borderRadius:'50%', background:'var(--pm-gradient)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, flexShrink:0 }}>
                    {r.employee?.name?.charAt(0) || '?'}
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:700, color:'#0f172a' }}>{r.employee?.name}</div>
                    <div style={{ fontSize:12, color:'#94a3b8' }}>{r.employee?.role} · Submitted {new Date(r.submittedAt||r.updatedAt).toLocaleTimeString()}</div>
                  </div>
                  <div style={{ fontSize:22 }} title={mc.label}>{mc.emoji}</div>
                  {r.hoursWorked && <span style={{ fontWeight:700, color:'#475569', fontSize:13 }}>{r.hoursWorked}h</span>}
                  {exp ? <FiChevronUp size={16} color="#94a3b8"/> : <FiChevronDown size={16} color="#94a3b8"/>}
                </div>
                {exp && (
                  <div style={{ padding:'0 20px 20px', borderTop:'1px solid #f1f5f9' }}>
                    {r.summary && (
                      <div style={{ marginTop:14 }}>
                        <div style={{ fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', marginBottom:6 }}>Today's Work</div>
                        <pre style={{ margin:0, fontFamily:'inherit', fontSize:13, color:'#0f172a', whiteSpace:'pre-wrap', background:'#f8fafc', padding:'10px 14px', borderRadius:10 }}>{r.summary}</pre>
                      </div>
                    )}
                    {r.blockers && (
                      <div style={{ marginTop:12 }}>
                        <div style={{ fontSize:11, fontWeight:700, color:'#ef4444', textTransform:'uppercase', marginBottom:6 }}>Blockers</div>
                        <pre style={{ margin:0, fontFamily:'inherit', fontSize:13, color:'#0f172a', whiteSpace:'pre-wrap', background:'#fff5f5', padding:'10px 14px', borderRadius:10, border:'1px solid #fee2e2' }}>{r.blockers}</pre>
                      </div>
                    )}
                    {r.tomorrow && (
                      <div style={{ marginTop:12 }}>
                        <div style={{ fontSize:11, fontWeight:700, color:'#3b82f6', textTransform:'uppercase', marginBottom:6 }}>Tomorrow's Plan</div>
                        <pre style={{ margin:0, fontFamily:'inherit', fontSize:13, color:'#0f172a', whiteSpace:'pre-wrap', background:'#eff6ff', padding:'10px 14px', borderRadius:10, border:'1px solid #bfdbfe' }}>{r.tomorrow}</pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
